import { Link } from "react-router-dom";
import { Instagram, Mail, Phone } from "lucide-react";
import PciSeal from "@/components/PciSeal";

export default function Footer() {
  return (
    <footer className="relative bg-[#0a0a0a] border-t border-white/5" data-testid="footer">
      {/* Watermark */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="watermark absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/3 whitespace-nowrap">
          RESURRECT
        </div>
      </div>

      <div className="container-tactical py-16 md:py-24 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#C0A062] rounded-sm flex items-center justify-center">
                <span className="font-heading font-black text-black text-xl">
                  RGP
                </span>
              </div>
              <div>
                <h2 className="font-heading font-bold text-white text-xl tracking-tight uppercase">
                  Resurrected Gun Parts
                </h2>
                <p className="font-mono text-xs text-[#C0A062] tracking-widest uppercase">
                  Rebuild. Restore. Resurrect.
                </p>
              </div>
            </Link>
            <p className="text-[#A1A1AA] max-w-md leading-relaxed mb-6">
              Specializing in reproduction parts for vintage military firearms.
              Keeping historic weapons alive and functional for generations to
              come.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/resurrectedgunparts"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center text-[#A1A1AA] hover:text-[#C0A062] hover:border-[#C0A062]/50 transition-all"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-white uppercase tracking-wider mb-6">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/shop"
                  className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors"
                  data-testid="footer-shop-link"
                >
                  Shop All Products
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors"
                  data-testid="footer-about-link"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/partner-program"
                  className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors"
                  data-testid="footer-partner-link"
                >
                  Partner Program
                </Link>
              </li>
              <li>
                <Link
                  to="/track-order"
                  className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors"
                  data-testid="footer-track-order-link"
                >
                  Track Your Order
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-heading font-bold text-white uppercase tracking-wider mb-6">
              Legal
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/terms" className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors" data-testid="footer-terms-link">
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors" data-testid="footer-privacy-link">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors" data-testid="footer-refund-link">
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to="/shipping-policy" className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors" data-testid="footer-shipping-link">
                  Shipping Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-white uppercase tracking-wider mb-6">
              Contact
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:admin@resurrectedgunparts.com"
                  className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#C0A062] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4" />
                  <span>admin@resurrectedgunparts.com</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+19286378133"
                  className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#C0A062] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4" />
                  <span>(928) 637-8133</span>
                </a>
              </li>
            </ul>
            <div className="mt-6 p-4 bg-[#121212] border border-white/5 rounded-sm">
              <p className="font-mono text-xs text-[#C0A062] uppercase tracking-wider mb-1">
                Payment Options
              </p>
              <p className="text-sm text-[#A1A1AA]">
                PayPal, Zelle, or Venmo
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[#71717a]">
            &copy; {new Date().getFullYear()} Resurrected Gun Parts. All rights
            reserved.
          </p>
          <div className="flex items-center gap-4">
            <PciSeal />
            <p className="font-mono text-xs text-[#71717a] tracking-wider">
              Founded 2024 • Preserving Firearm History
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
